'use client';
import { jsx, jsxs } from 'react/jsx-runtime';
import { omitThemingProps } from '@chakra-ui/styled-system';
import { callAll, cx } from '@chakra-ui/utils';
import { useCheckboxGroupContext, CheckboxStylesProvider, CheckboxContextProvider } from './checkbox-context.mjs';
import { splitCheckboxProps } from './checkbox-props.mjs';
import { useCheckbox } from './use-checkbox.mjs';
import { forwardRef } from '../system/forward-ref.mjs';
import { useMultiStyleConfig } from '../system/use-style-config.mjs';
import { chakra } from '../system/factory.mjs';

const CheckboxRoot = forwardRef(
  function Checkbox(props, ref) {
    const group = useCheckboxGroupContext();
    const mergedProps = { ...group, ...props };
    const styles = useMultiStyleConfig("Checkbox", mergedProps);
    const ownProps = omitThemingProps(props);
    const {
      spacing = "0.5rem",
      className,
      children,
      isChecked: isCheckedProp,
      isDisabled = group?.isDisabled,
      onChange: onChangeProp,
      inputProps,
      ...rest
    } = ownProps;
    let isChecked = isCheckedProp;
    if (group?.value && ownProps.value) {
      isChecked = group.value.includes(ownProps.value);
    }
    let onChange = onChangeProp;
    if (group?.onChange && ownProps.value) {
      onChange = callAll(group.onChange, onChangeProp);
    }
    const [checkboxProps, localProps] = splitCheckboxProps(rest);
    const checkboxState = useCheckbox({
      ...checkboxProps,
      isDisabled,
      isChecked,
      onChange
    });
    const { getRootProps, getInputProps } = checkboxState;
    return /* @__PURE__ */ jsx(CheckboxStylesProvider, { value: styles, children: /* @__PURE__ */ jsx(CheckboxContextProvider, { value: { ...checkboxState, spacing }, children: /* @__PURE__ */ jsxs(
      chakra.label,
      {
        ...getRootProps(localProps),
        __css: styles.root,
        className: cx("chakra-checkbox", className),
        children: [
          /* @__PURE__ */ jsx(
            "input",
            {
              ...getInputProps(inputProps, ref),
              className: "chakra-checkbox__input"
            }
          ),
          children
        ]
      }
    ) }) });
  }
);
CheckboxRoot.displayName = "Checkbox";

export { CheckboxRoot };
